import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import {
  getMembershipSatisfactionSurvey,
  submitMembershipSatisfactionSurvey,
} from '@/pages/api/feedback';
import s from './style.module.scss';

const SCORE_LABELS = ['Poor', 'Fair', 'Good', 'Excellent'];
const STAR_ICON =
  'https://cdn.duetapp.net/v1/images/eyJpZCI6Ik1TVkxMSkFSQ1E1TUlKWFlZU1Y2RkhVTVFaVFBFQiIsInciOjQ4LCJoIjo0OCwiZCI6MCwibXQiOiJpbWFnZS9qcGVnIiwiZGgiOjYxMzkyOTgxODI2MjExMDUyMjl9.webp';
const DONE_ICON =
  'https://cdn.duetapp.net/v1/images/eyJpZCI6IlJGNlhCR0tXTUhRRDU1N0Q0WDRERzY1Nzc3MzVEUSIsInciOjY4LCJoIjo2OCwiZCI6MCwibXQiOiJpbWFnZS9qcGVnIiwiZGgiOjE2ODcxMDE3MzY0OTMwMTk0MTV9.webp';

const COPY = {
  title: 'How did we do?',
  subtitle:
    'If our matchmaking service made your experience a great one, please take a quick moment to rate us. Your feedback helps us continue improving Duet Matchmaking.',
  placeholder: "Anything you'd like to share about your experience? (Optional)",
  submit: 'Submit feedback',
  toast: 'Please provide feedback',
  thankTitle: 'Thank you for your feedback!',
  thankSubtitle:
    'We appreciate your feedback and will use it to continue delivering thoughtful introductions.',
  error: 'Something went wrong. Please try again later.',
  notFound: 'No feedback invitation found.',
};

export default function Feedback() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('');
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState('');
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getMembershipSatisfactionSurvey();
        const data = result?.data?.membershipSatisfactionSurvey;
        if (!data) {
          if (!cancelled) setError(COPY.notFound);
          return;
        }
        if (!cancelled) {
          setStatus(data.status || '');
          if (data.status === 'submitted') {
            setScore(data.score || 0);
          }
        }
      } catch (e) {
        if (!cancelled) {
          const msg = String(e?.message || e || '');
          if (msg.includes('404') || msg.includes('Not Found')) {
            setError(COPY.notFound);
          } else {
            setError(COPY.error);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const showForm = !loading && !error && status === 'pending';
  const showDone = !loading && !error && status === 'submitted';

  const showToast = (text) => {
    setToast(text);
    window.setTimeout(() => setToast(''), 2200);
  };

  const handleSubmit = async () => {
    if (score < 1) {
      showToast(COPY.toast);
      return;
    }
    setSubmitting(true);
    try {
      await submitMembershipSatisfactionSurvey({ score, comment });
      setStatus('submitted');
    } catch (e) {
      showToast(COPY.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Head>
        <title>Duet Matchmaking Feedback</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      </Head>
      <div className={s.page}>
        {loading && <div className={s.state}>Loading...</div>}
        {error && <div className={s.state}>{error}</div>}

        {showForm && (
          <div className={s.card}>
            <div className={s.title}>{COPY.title}</div>
            <p className={s.subtitle}>{COPY.subtitle}</p>

            <div className={s.stars}>
              {[1, 2, 3, 4].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`${s.starItem} ${n <= score ? s.starOn : ''}`}
                  onClick={() => setScore(n)}
                  aria-label={SCORE_LABELS[n - 1]}
                >
                  <img className={s.star} src={STAR_ICON} alt="" draggable={false} />
                  <span className={s.starLabel}>{SCORE_LABELS[n - 1]}</span>
                </button>
              ))}
            </div>

            <textarea
              className={s.textarea}
              placeholder={COPY.placeholder}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={2000}
            />

            <button
              type="button"
              className={`${s.submit} ${score < 1 ? s.submitDisabled : ''}`}
              disabled={submitting}
              onClick={handleSubmit}
            >
              {COPY.submit}
            </button>
          </div>
        )}

        {showDone && (
          <div className={s.done}>
            <img className={s.doneIcon} src={DONE_ICON} alt="" draggable={false} />
            <div className={s.title}>{COPY.thankTitle}</div>
            <p className={s.subtitle}>{COPY.thankSubtitle}</p>
          </div>
        )}

        {toast ? <div className={s.toast}>{toast}</div> : null}
      </div>
    </>
  );
}
